import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';

/** Which kind of pasted product list narrows the Pricy scope. */
export type PricyListKind = 'ean' | 'vendor_code';

export interface PricyScopeInput {
  brand: string;
  product_list_kind: PricyListKind;
  /** Pasted multi-line text — the backend splits on newlines/commas. */
  product_list: string;
}

export interface PricyPreviewResult {
  status: string;
  vendor: string;
  product_number: number;
  product_variant_number: number;
  unmatched_eans?: string[];
  unmatched_vendor_codes?: string[];
  vendor_exists?: boolean;
}

export interface PricyCreateInput extends PricyScopeInput {
  discount_value: number;
  pricy_status: 'Active' | 'Draft';
}

export interface PricyCreateResult {
  created: boolean;
  pricy_campaign_id: number;
  pricy_campaign_url: string | null;
  scope_summaries?: Array<{
    product_number: number;
    product_variant_number: number;
    unmatched_eans?: string[];
    unmatched_vendor_codes?: string[];
  }>;
}

export const usePricyPreview = (campaignId: string) =>
  useMutation({
    mutationFn: async (payload: PricyScopeInput): Promise<PricyPreviewResult> => {
      const res = await axios.post(`api/campaigns/${campaignId}/pricy/preview/`, payload);
      return res.data;
    },
  });

export const useCreatePricyCampaign = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PricyCreateInput): Promise<PricyCreateResult> => {
      const res = await axios.post(`api/campaigns/${campaignId}/pricy/create/`, payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

/** One Pricy scope of the linked campaign, as the Brands & Discounts overview shows it. */
export interface PricyBrandDiscount {
  scope_id: number;
  brand: string;
  scope: string;
  enabled: boolean;
  discount_type: string;
  discount_min: number | null;
  discount_max: number | null;
  /** Ready to show: "20.1%", "15–23%" (Margin range), "€10" (Absolute). */
  discount: string;
  start_date: string | null;
  end_date: string | null;
  markets: string[];
  till_stockout: boolean;
}

export interface PricyDiscountsResult {
  pricy_campaign_id: number;
  pricy_campaign_url: string;
  pricy_campaign: { name: string | null; status: string | null; start_date: string | null; end_date: string | null };
  counts: { on: number; off: number };
  brands: PricyBrandDiscount[];
}

/** Read live from Pricy on every visit — nothing is stored in mosy. */
export const usePricyDiscounts = (campaignId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['campaign', campaignId, 'pricy-discounts'],
    queryFn: async (): Promise<PricyDiscountsResult> => {
      const res = await axios.get(`api/campaigns/${campaignId}/pricy/discounts/`);
      return res.data;
    },
    enabled: !!campaignId && enabled,
  });

export const downloadPricyDiscountsXlsx = async (campaignId: string, campaignName: string): Promise<void> => {
  const res = await axios.get(`api/campaigns/${campaignId}/pricy/discounts/`, {
    params: { export: 'xlsx' },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${campaignName} - Brands & Discounts.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
